import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/lib/AuthContext";
import LangToggle from "@/components/LangToggle";
import EmergencyButton from "@/components/EmergencyButton";

export default function AppShell() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user || !user.id) return null;

  return (
    <div className="min-h-screen bg-[var(--bg)] flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-[var(--border)] bg-[var(--surface)] sticky top-0 h-screen overflow-y-auto" data-testid="app-sidebar">
        <div className="px-6 py-7">
          <Link to="/" className="font-display text-2xl">Mordomo</Link>
          <p className="label-cap mt-2 text-[var(--ink-faint)]">— Ao seu dispor</p>
        </div>
        <nav className="flex-1 px-3 py-1 space-y-px">
          <SideLink to="/app" end label={t("app.sidebar.dashboard")} testId="side-dashboard" />
          <SideLink to="/app/chat" label={t("app.sidebar.chat")} testId="side-chat" />
          <SideLink to="/app/ingest" label={t("app.sidebar2.ingest")} testId="side-ingest" />
          <SideLink to="/app/proposals" label={t("app.sidebar2.proposals")} testId="side-proposals" />
          <SideLink to="/app/calendar" label={t("app.sidebar2.calendar")} testId="side-calendar" />
          <SideLink to="/app/shopping" label={t("app.sidebar2.shopping")} testId="side-shopping" />
          <SideLink to="/app/recipes" label={t("app.sidebar2.recipes")} testId="side-recipes" />
          <SideLink to="/app/family" label={t("app.sidebar2.family")} testId="side-family" />
          <SideLink to="/app/family/agreements" label={t("app.sidebar2.agreements")} testId="side-agreements" />
          <SideLink to="/app/appointments" label={t("app.sidebar2.appointments")} testId="side-appointments" />
          <SideLink to="/app/medications" label={t("app.sidebar2.medications")} testId="side-medications" />
          <SideLink to="/app/memory" label={t("app.sidebar2.memory")} testId="side-memory" />
          <SideLink to="/app/connect/gmail" label={t("app.sidebar2.connectGmail")} testId="side-gmail" />
          <SideLink to="/app/diary" label={t("app.sidebar.diary")} testId="side-diary" />
          <SideLink to="/app/settings" label={t("app.sidebar.settings")} testId="side-settings" />
        </nav>
        <div className="px-6 py-5 border-t border-[var(--border)] space-y-4">
          <div>
            <p className="label-cap">— Chefe</p>
            <p className="font-display text-lg mt-1" data-testid="sidebar-user-name">{user.name}</p>
            <p className="text-xs text-[var(--ink-faint)] mt-1">{user.email}</p>
          </div>
          <div className="flex items-center justify-between">
            <LangToggle />
            <button
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="btn-ghost"
              data-testid="sidebar-logout-btn"
            >
              {t("nav.logout")}
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <Link to="/" className="font-display text-2xl">Mordomo</Link>
          <LangToggle />
        </div>
        <div className="md:hidden flex overflow-x-auto no-scrollbar scroll-fade-x border-b border-[var(--border)]">
          <MobileTab to="/app" end label={t("app.sidebar.dashboard")} testId="mside-dashboard" />
          <MobileTab to="/app/chat" label={t("app.sidebar.chat")} testId="mside-chat" />
          <MobileTab to="/app/ingest" label={t("app.sidebar2.ingest")} testId="mside-ingest" />
          <MobileTab to="/app/proposals" label={t("app.sidebar2.proposals")} testId="mside-proposals" />
          <MobileTab to="/app/calendar" label={t("app.sidebar2.calendar")} testId="mside-calendar" />
          <MobileTab to="/app/shopping" label={t("app.sidebar2.shopping")} testId="mside-shopping" />
          <MobileTab to="/app/recipes" label={t("app.sidebar2.recipes")} testId="mside-recipes" />
          <MobileTab to="/app/family" label={t("app.sidebar2.family")} testId="mside-family" />
          <MobileTab to="/app/family/agreements" label={t("app.sidebar2.agreements")} testId="mside-agreements" />
          <MobileTab to="/app/appointments" label={t("app.sidebar2.appointments")} testId="mside-appointments" />
          <MobileTab to="/app/medications" label={t("app.sidebar2.medications")} testId="mside-medications" />
          <MobileTab to="/app/memory" label={t("app.sidebar2.memory")} testId="mside-memory" />
          <MobileTab to="/app/connect/gmail" label={t("app.sidebar2.connectGmail")} testId="mside-gmail" />
          <MobileTab to="/app/diary" label={t("app.sidebar.diary")} testId="mside-diary" />
          <MobileTab to="/app/settings" label={t("app.sidebar.settings")} testId="mside-settings" />
        </div>
        <Outlet />
      </main>

      <EmergencyButton />
    </div>
  );
}

function SideLink({ to, label, end, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center justify-between px-4 py-2.5 text-[12px] tracking-monocle uppercase transition-colors duration-300 ${
          isActive
            ? "bg-[var(--bg)] text-[var(--ink)] border-l-2 border-[var(--oxblood)]"
            : "text-[var(--ink-soft)] hover:text-[var(--ink)] border-l-2 border-transparent"
        }`
      }
    >
      <span>{label}</span>
    </NavLink>
  );
}

function MobileTab({ to, label, end, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `tap-target inline-flex items-center flex-none px-5 text-[12px] tracking-monocle uppercase whitespace-nowrap ${
          isActive ? "text-[var(--ink)] border-b-2 border-[var(--oxblood)]" : "text-[var(--ink-soft)]"
        }`
      }
    >
      {label}
    </NavLink>
  );
}
